import React, { useEffect, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { FlightTakeoff, Person, Speed, Shield } from '@mui/icons-material';
import { motion } from 'framer-motion';
import type { LandingCopy, LandingPalette } from './landingCopy';

const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({
  end,
  duration = 1600,
  suffix = '',
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let frame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [end, duration]);

  return (
    <>
      {count.toLocaleString()}
      {suffix}
    </>
  );
};

export type LiveOpsStats = {
  totalIncidents: number;
  activeUsers: number;
  responseTime: string;
  slaCompliance: number | null;
};

type LiveOpsSnapshotProps = {
  stats: LiveOpsStats;
  loading?: boolean;
  copy: LandingCopy['stats'];
  palette: LandingPalette;
};

const LiveOpsSnapshot: React.FC<LiveOpsSnapshotProps> = ({ stats, loading, copy, palette }) => {
  const items = [
    {
      label: copy.totalIncidents,
      value: stats.totalIncidents,
      icon: <FlightTakeoff sx={{ color: '#7DD3FC' }} />,
      numeric: true,
    },
    {
      label: copy.activeUsers,
      value: stats.activeUsers,
      icon: <Person sx={{ color: '#4ADE80' }} />,
      numeric: true,
    },
    {
      label: copy.avgResponse,
      value: stats.responseTime,
      icon: <Speed sx={{ color: '#FBBF24' }} />,
      numeric: false,
    },
    {
      label: copy.sla,
      value: stats.slaCompliance == null ? '—' : stats.slaCompliance,
      suffix: stats.slaCompliance == null ? '' : '%',
      icon: <Shield sx={{ color: '#60A5FA' }} />,
      numeric: stats.slaCompliance != null,
    },
  ];

  return (
    <Box
      id="stats"
      component="section"
      sx={{
        py: { xs: 4, md: 5 },
        bgcolor: palette.pageBg,
        background: `linear-gradient(180deg, ${palette.pageBg} 0%, ${palette.sectionAlt} 100%)`,
        borderTop: `1px solid ${palette.border}`,
        borderBottom: `1px solid ${palette.border}`,
        color: palette.text,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            fontFamily: '"Outfit", "Cairo", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            mb: 1,
          }}
        >
          {copy.title}
        </Typography>
        <Typography sx={{ color: palette.muted, mb: 2.5, maxWidth: 520 }}>
          {copy.subtitle}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2.5,
          }}
        >
          {items.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: index * 0.08, duration: 0.45 }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  minHeight: 140,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  {item.icon}
                  <Typography
                    variant="caption"
                    sx={{
                      color: palette.mutedSoft,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontFamily: '"Outfit", "Cairo", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {loading ? (
                    '—'
                  ) : item.numeric ? (
                    <AnimatedCounter end={Number(item.value)} suffix={item.suffix} />
                  ) : (
                    `${item.value}${item.suffix || ''}`
                  )}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default LiveOpsSnapshot;
